import { MODE_SELECTOR } from 'constants/app';

export enum ContentfulContentTypes {
  landingPageBannerGroup = 'landingPageBannerGroup',
  landingPageBigBanner = 'landingPageBigBanner',
  landingPageMediumBlockGroup = 'landingPageMediumBlockGroup',
  landingPageSpecialOffersGroup = 'landingPageSpecialOffersGroup',
  landingPageHtml = 'landingPageHtml',
  landingPageMessageCardGroup = 'landingPageMessageCardGroup',
  landingPageSteps = 'landingPageSteps',
  landingPageCatalog = 'landingPageCatalog',
  landingPageCatalogItem = 'landingPageCatalogItem',
  landingPageSeparator = 'landingPageSeparator',
  landingPageStickyBar = 'stickyBar',
  landingPageDownloadAppOrSendSms = 'downloadAppOrSendSms',
  landingPageButtonNativeAppDownloadFromStore = 'buttonNativeAppDownloadFromStore',
  landingPageButton = 'button',
  landingPageUniqueSellingPointText = 'landingPageUniqueSellingPointText',
  pixleeWidget = 'pixleeWidget',
  blueShiftRecommendationBlock = 'blueShiftRecommendationBlock',
  landingPageSmallBlockGroup = 'landingPageSmallBlockGroup',
  landingPageBigBlockGroup = 'landingPageBigBlockGroup',
  landingPageLastOrderDate = 'justPlaceholder2',
}

export type FontWeight = 'normal' | 'bold' | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

export type Theme = 'studio' | 'default' | 'landing' | 'shopify';

export interface HelloBar {
  text: string;
  backgroundColor: string;
  textColor: string;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface Button {
  label: string;
  style: 'primary' | 'secondary';
  size: 'content' | 'default';
  referenceUrl: string;
  discount?: any;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface ButtonNativeApp {
  contentType: string;
  currentTargetDevice: string;
  entryId: string;
  name: string;
  stores: string[];
  text: string;
}

export interface ButtonItem {
  button: Button;
  buttonNativeApp: ButtonNativeApp;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface Reference {
  buttonItem: ButtonItem;
  miniBanner?: any;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface MediumBanner {
  miniBanner?: any;
  title: string;
  layout: string;
  bannerImagePosition?: any;
  description1: string;
  description1Preview?: any;
  icons: any[];
  description2: string;
  description2Preview?: any;
  references: Reference[];
  referenceItemsLayout?: any;
  titleFontColor?: any;
  titleFontWeight?: any;
  description1FontColor?: any;
  description1FontWeight?: any;
  description2FontColor?: any;
  description2FontWeight?: any;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface MediumBannerGroup {
  heading?: any;
  title?: any;
  containerClasses?: any;
  headingFontColor?: any;
  headingFontWeight?: any;
  titleFontColor?: any;
  titleFontWeight?: any;
  cardsStyle?: any;
  messageCards: MediumBanner[];
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface Banner {
  h1: string;
  renderH1Desktop: boolean;
  renderH1Mobile: boolean;
  renderH2Desktop: boolean;
  renderH2Mobile: boolean;
  h1Color: string;
  h1ColorPhone: string;
  h1FontWeight: FontWeight;
  h2: string;
  h2Color: string;
  h2ColorPhone: string;
  h2FontWeight?: FontWeight;
  buttonLabel: string;
  buttonColor: string;
  buttonBackgroundColor: string;
  buttonBorderColor: string;
  referenceUrl: string;
  bannerImageUrl: string;
  bannerImageUrlDesktop: string;
  bannerImageUrlTablet: string;
  bannerImageUrlMobile: string;
  textAlignment: string;
  referenceVoucher?: any;
  theme: string;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
  features: string[];
  appDownloadButtons: boolean;
}

export interface BigBanner {
  description?: string;
  bannerImageUrl?: string;
  bannerImageUrlDesktop?: string;
  bannerImageUrlTablet?: string;
  bannerImageUrlMobile?: string;
  hideScrollIcon?: boolean;
  textAlignment?: string;
  titleFontWeight?: null;
  descriptionFontWeight?: null;
  reference?: ButtonItem;
  entryId?: string;
  name?: string;
  title?: string;
  contentType?: string;
  currentTargetDevice?: string;
  height: number;
  heightMobile: number;
  h1: string;
  h1Color: string;
  h1FontWeight: FontWeight;
  h2: string;
  h2Color: string;
  h2FontWeight: FontWeight;
  buttonLabel: string;
  buttonColor: string;
  buttonBackgroundColor: string;
  buttonBorderColor: string;
  theme: string;
  features: string[];
  headingTag?: string;
}

export interface BannerGroup {
  height: number;
  heightDesktop: number;
  heightTablet?: number;
  heightMobile: number;
  banners: Banner[];
  backgroundColor?: any;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface Html {
  content: string;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export type MediumBlockGroupType = 'default' | 'portrait' | 'portrait-grid' | 'portrait-rounded';
export interface Block {
  title: string;
  subtitle?: string;
  imageUrl: string;
  imageUrlDesktop: string;
  imageUrlTablet: string;
  imageUrlMobile: string;
  imageUrlTabletPortrait: string;
  referenceLabel: string;
  referenceUrl: string;
  discount?: any;
  titleColor?: any;
  referenceLabelColor: string;
  referenceBackgroundColor: string;
  referenceBorderColor: string;
  titlePosition?: any;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
  type: MediumBlockGroupType;
  label?: string;
  priceFormatted?: string;
}

export interface MediumBlockGroup {
  blocks: Block[];
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
  component: 'carousel' | 'grid';
  title?: string;
  variant?: MediumBlockGroupType;
  seeAllUrl?: string;
  seeAllLabel?: string;
}

export interface SpecialOffers {
  title: string;
  bannerImageUrl: string;
  bannerImageUrlDesktop: string;
  bannerImageUrlTablet: string;
  bannerImageUrlMobile: string;
  discount: {
    voucherCode: string;
    voucherExpirationDate: string;
    label: string;
    fontColor: string;
    bubbleBackgroundColor: string;
    bubbleBorderColor: string;
    entryId: string;
    name: string;
    contentType: string;
    currentTargetDevice: string;
  };
  descriptionLines: any[];
  button: {
    label: string;
    style: string;
    size: string;
    referenceUrl: string;
    discount: string;
    entryId: string;
    name: string;
    contentType: string;
    currentTargetDevice: string;
  };
  offerExpiration: string;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface SpecialOffersGroup {
  heading: string;
  subheading: string;
  specialOffers: SpecialOffers[];
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface CatalogItem {
  abstractProduct: {
    abstractProductId: number;
    categoryId: number;
    products: { productId: number }[];
    initialProduct: { productId: number };
  };
  specificAbstractProduct: string;
  badgeText: string;
  badgeTheme: string;
  contentType: string;
  currentTargetDevice: string;
  entryId: string;
  name: string;
  priceTranslationKey: string;
  product: any;
  productImageUrl: string;
  productImageUrlDesktop: string;
  productImageUrlMobile: string;
  productImageUrlTablet: string;
  referenceProductName: string;
  referenceUrl: string;
  title: string;
  upsellHighlightTranslationKey: string;
}

export interface Catalog {
  contentType: string;
  currentTargetDevice: string;
  defaultSelected: CatalogItem | null | undefined;
  entryId: string;
  items: CatalogItem[];
  mode: MODE_SELECTOR;
  name: string;
  title: string;
  vatNote: string;
  variant: 'carousel' | 'grid';
}

export interface Separator {
  contentType: string;
  currentTargetDevice: string;
  entryId: string;
  name: string;
  paddingBottom: number;
  paddingBottomPhone: number;
  paddingTop: number;
  paddingTopPhone: number;
  showVerticalLine: boolean;
}

export interface StickyBar {
  text: string;
  name: string;
  textColor: string;
  contentType: string;
  entryId: string;
  currentTargetDevice: string;
}

export interface RecommendationBlock {
  slotName: string;
  categoryId: number;
  subCategoryId?: number;
  title: string;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
  liveRecommendations?: boolean;
  preset?: string;
  description?: string;
}

export interface DownloadAppOrSendSms {
  h1: string;
  h1Desktop: string;
  h1Mobile: string;
  h1Tablet: string;
  h2: string;
  h2Desktop: string;
  h2Mobile: string;
  h2Tablet: string;
  h3: string;
  h3Desktop: string;
  h3Mobile: string;
  h3Tablet: string;
  name: string;
  phoneAndroidImageUrl: string;
  phoneImageUrl: string;
  sendAnotherSms: string;
  smsButtonLabel: string;
  smsPhoneBigImageUrl: string;
  sendSmsButton: {
    label: string;
  };
  downloadAppButton: ButtonNativeApp;
}

export interface PixleeWidget {
  enabled: boolean;
  name: string;
  widgetId: number;
  widgetType: string;
  skuId: number;
  categoryName: string;
  categoryId: number;
  fallbackWidget: PixleeWidget;
}

export interface SmallBlock {
  title: string;
  imageUrl: string;
  imageUrlDesktop: string;
  imageUrlTablet: string;
  imageUrlMobile: string;
  imageUrlTabletPortrait: string;
  referenceLabel: string;
  referenceUrl: string;
  discount: {
    voucherCode: string;
    voucherExpirationDate: string;
    label: string;
    fontColor: string;
    bubbleBackgroundColor: string;
    bubbleBorderColor: string;
    entryId: string;
    name: string;
    contentType: string;
    currentTargetDevice: string;
  };
  titleColor: string;
  referenceLabelColor: string;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface SmallBlockGroup {
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
  blocks: SmallBlock[];
}

export interface BigBlock {
  title: string;
  description: string;
  imageUrl: string;
  imageUrlDesktop: string;
  imageUrlTablet: string;
  imageUrlMobile: string;
  imageUrlTabletPortrait: string;
  referenceLabel: string;
  referenceUrl: string;
  discount: {
    voucherCode: string;
    voucherExpirationDate: string;
    label: string;
    fontColor: string;
    bubbleBackgroundColor: string;
    bubbleBorderColor: string;
    entryId: string;
    name: string;
    contentType: string;
    currentTargetDevice: string;
  };
  titleColor: string;
  descriptionColor: string;
  referenceLabelColor: string;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface BigBlockGroup {
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
  blocks: BigBlock[];
}

export interface LastOrderDateBlock {
  title: string;
  description: string;
  productColumnTitle: string;
  standardShippingColumnTitle: string;
  expressShippingColumnTitle: string;
  standardShippingColumnTitleShort: string;
  expressShippingColumnTitleShort: string;
}

export interface Content {
  miniBannerGroup?: any;
  recommendationBlock?: RecommendationBlock;
  productCardGroup?: any;
  messageCardGroup: MediumBannerGroup;
  bannerGroup: BannerGroup;
  trustpilot?: any;
  bigBanner?: BigBanner;
  steps?: SeriesSteps;
  html: Html;
  paymentResultOrderDetails?: any;
  downloadAppOrSendSms?: DownloadAppOrSendSms;
  separator?: Separator;
  bigBlockGroup?: any;
  mediumBlockGroup: MediumBlockGroup;
  smallBlockGroup?: SmallBlockGroup;
  specialOffersGroup: SpecialOffersGroup;
  catalog?: Catalog;
  catalogABTested?: any;
  crossSell?: any;
  pixleeWidget?: PixleeWidget;
  lastOrderDateBlock?: LastOrderDateBlock;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
  catalogItems: any[];
}

export interface UniqueSellingPoints {
  text: {
    title: string;
    entryId: string;
    name: string;
    contentType: string;
    currentTargetDevice: string;
  };
  trustpilot: {
    entryId: string;
    name: string;
    contentType: string;
    currentTargetDevice: string;
  };
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface LandingPageData {
  pageId: string;
  urlMapping: string;
  layout: string;
  helloBar: HelloBar;
  stickyBar?: StickyBar;
  bannerGroup?: BannerGroup;
  content: Content[];
  uniqueSellingPoints: UniqueSellingPoints[];
  fullUrl: string;
  imageUrl?: any;
  searchText?: any;
  title?: any;
  countdown?: any;
  theme: Theme;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
  catalogItems: GroupedCatalog[];
  showCampaignContent: boolean;
}

export type GroupedCatalogItem = Record<
  string,
  {
    productImageUrl: string;
    badgeTheme: string;
    badgeText: string;
    items: {
      price: string;
      price_discounted: string;
      label: string;
      url: string;
      badgeTheme: string;
      badgeText: string;
    }[];
  }
>;

export interface GroupedCatalog {
  entryId: string;
  items: GroupedCatalogItem;
}

export interface SeriesStepItem {
  title: string;
  imageUrl: string;
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface SeriesSteps {
  title?: string;
  description?: string;
  items: SeriesStepItem[];
  entryId: string;
  name: string;
  contentType: string;
  currentTargetDevice: string;
}

export interface Discount {
  bubbleBackgroundColor: string;
  bubbleBorderColor: string;
  contentType: string;
  currentTargetDevice: string;
  entryId: string;
  fontColor: string;
  label: string;
  name: string;
  voucherCode: string;
  voucherExpirationDate: string;
}
